/**
 * MarkdownEditorPage (epic-6 §4 / ST-3/ST-4) — route /storage/files/:fileId/markdown.
 *
 * Monaco (language: markdown) on the left, live renderMarkdown preview on the
 * right. Save = PUT /storage/files/:id/markdown; Append dialog issues a PATCH
 * append (base64) to the partial content-edit endpoint. The version badge
 * reflects the file's `versionCount`.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, FilePlus2, History, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/shared/Toast';
import { CodeEditor } from '@/components/webhooks/CodeEditor';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { renderMarkdown } from '@/lib/markdown';
import { downloadFile } from '@/lib/storage-transfer';
import { useGetStorageFileQuery } from '@/store/api/apiSlice';
import { useSaveFileMarkdownMutation, useEditFileContentMutation } from '@/store/api/storageContentApi';

/** Encode a JS string as base64 (UTF-8 safe). */
export function utf8ToBase64(text: string): string {
  try {
    return btoa(unescape(encodeURIComponent(text)));
  } catch {
    return btoa(text);
  }
}

export function MarkdownEditorPage() {
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const { data: file } = useGetStorageFileQuery({ fileId: fileId ?? '' }, { skip: !fileId });
  const [saveMarkdown, { isLoading: isSaving }] = useSaveFileMarkdownMutation();
  const [editContent, { isLoading: isAppending }] = useEditFileContentMutation();

  const [content, setContent] = useState('');
  const [loadingContent, setLoadingContent] = useState(true);
  const [appendOpen, setAppendOpen] = useState(false);
  const [appendText, setAppendText] = useState('');

  const isMarkdown =
    file?.extension?.toLowerCase() === 'md' ||
    file?.mimeType === 'text/markdown' ||
    file?.fileName?.toLowerCase().endsWith('.md');

  useEffect(() => {
    if (!fileId) return;
    let cancelled = false;
    setLoadingContent(true);
    downloadFile(fileId)
      .then(async ({ blob }) => {
        const text = await blob.text();
        if (!cancelled) setContent(text);
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Could not load file content');
          setContent('');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingContent(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  const handleSave = async () => {
    try {
      // Stay on the editor after saving so the user keeps their editing
      // context. The version badge refreshes automatically because
      // `saveFileMarkdown` invalidates the `{ type: 'StorageFile', id: fileId }`
      // tag provided by `useGetStorageFileQuery`.
      await saveMarkdown({
        fileId: fileId!,
        body: { content, expectedVersion: file?.versionCount },
      }).unwrap();
      toast.success('Markdown saved');
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to save markdown');
    }
  };

  const handleAppend = async () => {
    if (!appendText.trim()) return;
    try {
      await editContent({
        fileId: fileId!,
        body: { op: 'append', data: utf8ToBase64(appendText) },
      }).unwrap();
      toast.success('Append enqueued — content will refresh on completion');
      setAppendOpen(false);
      setAppendText('');
      // Refetch content after the async file-edit finishes.
      window.setTimeout(async () => {
        try {
          const { blob } = await downloadFile(fileId!);
          setContent(await blob.text());
        } catch {
          /* best effort */
        }
      }, 2000);
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Append failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(file?.collectionId ? `/storage/collections/${file.collectionId}/files` : '/storage/collections')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <FilePlus2 className="h-5 w-5 text-muted-foreground" />
            {file?.fileName ?? 'Markdown editor'}
            <Badge variant="outline" className="text-[10px] font-mono">
              v{(file?.versionCount ?? 1)}
            </Badge>
            {!isMarkdown && <Badge variant="secondary" className="text-[10px]">text file</Badge>}
          </h1>
          <p className="text-sm text-muted-foreground">
            {file?.description ?? 'Markdown edit — Save replaces the whole file (PUT), Append patches the tail.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setAppendOpen(true)} disabled={isAppending}>
            <History className="mr-1 h-4 w-4" /> {isAppending ? 'Appending…' : 'Append'}
          </Button>
          <Button onClick={handleSave} disabled={isSaving || loadingContent} className="min-w-[120px]">
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="py-2.5 px-3">
            <CardTitle className="text-sm">Source (markdown)</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0">
            <div className="overflow-hidden rounded-md border">
              <CodeEditor
                language="markdown"
                value={content}
                onChange={setContent}
                height={560}
              />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="py-2.5 px-3">
            <CardTitle className="text-sm">Preview</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div
              className="max-h-[560px] overflow-auto rounded-md border bg-background p-4 text-sm leading-relaxed prose prose-neutral prose-sm max-w-none"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
            />
          </CardContent>
        </Card>
      </div>

      {/* Append dialog */}
      <Dialog open={appendOpen} onOpenChange={setAppendOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Append content</DialogTitle>
            <DialogDescription>
              The appended text will be base64-encoded and enqueued as an async file-edit (PATCH append).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Text to append</Label>
            <textarea
              autoFocus
              rows={6}
              className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={appendText}
              placeholder="…continues the document"
              onChange={(e) => setAppendText(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAppendOpen(false)}>Cancel</Button>
            <Button onClick={handleAppend} disabled={!appendText.trim() || isAppending}>
              {isAppending ? 'Appending…' : 'Append'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}